import React, { useCallback, useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Send, ShieldAlert, FileSearch, Sparkles, ExternalLink, Coins, BookmarkPlus, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/apiClient";
import { useAiConfirm } from "@/components/AiConfirm";
import { Justice, JusticeThinking } from "@/components/Justice";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";

const SUGGESTIONS = [
  "How many exchange-schedule issues are documented this month?",
  "What do the school records document about attendance?",
  "What does my evidence document — and not document — about missed exchanges?",
];

function Citation({ c, onOpen }) {
  return (
    <button
      onClick={() => c.ref && onOpen(c.ref)}
      disabled={!c.ref}
      data-testid={`citation-${c.evidence_id}`}
      className="inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-xs mr-1.5 mb-1.5 transition-colors"
      style={{ borderColor: "rgba(46,130,246,0.35)", color: c.ref ? "var(--ek-primary)" : "var(--ek-text-muted)", background: "var(--ek-page)", cursor: c.ref ? "pointer" : "default" }}
      title={c.note || c.location}
    >
      <FileSearch className="h-3 w-3" /> {c.evidence_id}{c.location ? ` · ${c.location}` : ""}
      {c.ref && <ExternalLink className="h-2.5 w-2.5" />}
    </button>
  );
}

export default function AskJustice({ matterId, evidenceCount = 0, onOpenEvidence }) {
  const { confirmAi } = useAiConfirm();
  const [msgs, setMsgs] = useState([]);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [saveTarget, setSaveTarget] = useState(null); // { answer, question }
  const [saving, setSaving] = useState(false);
  const endRef = useRef(null);

  const saveAnswer = async (mode) => {
    if (!saveTarget) return;
    setSaving(true);
    try {
      await api.post("/justice/save-answer", { answer: saveTarget.answer, question: saveTarget.question, mode });
      toast.success(
        mode === "both" ? "Saved to timeline & case objective"
          : mode === "objective" ? "Appended to case objective" : "Saved as a timeline record");
      setSaveTarget(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save");
    } finally { setSaving(false); }
  };

  const load = useCallback(() => {
    api.get("/justice/history").then(({ data }) => {
      const flat = [];
      (data.conversations || []).forEach((c) => {
        flat.push({ role: "user", text: c.question });
        flat.push({ role: "justice", text: c.answer, citations: c.citations, cached: c.cached });
      });
      setMsgs(flat);
    }).catch((e) => console.error("history failed", e));
  }, []);

  useEffect(() => { load(); }, [load, matterId]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs, busy]);

  const ask = async (question) => {
    const text = (question || q).trim();
    if (!text || busy) return;
    const ok = await confirmAi({
      action: "justice_qa",
      title: "Ask Justice",
      description: "Justice will search this case's Evidence Vault and answer using only what your evidence documents, with citations. Re-asking the same question on unchanged evidence is free.",
    });
    if (!ok) return;
    setQ("");
    setMsgs((m) => [...m, { role: "user", text }]);
    setBusy(true);
    try {
      const { data } = await api.post("/justice/ask", { question: text });
      setMsgs((m) => [...m, { role: "justice", text: data.answer, citations: data.citations, cached: data.cached, suggestions: data.suggestions || [] }]);
      if (data.cached) toast.message("Cached answer — no KeeperCredits used (evidence unchanged).");
    } catch (e) {
      const detail = e.response?.data?.detail || "Justice is temporarily unavailable.";
      setMsgs((m) => [...m, { role: "justice", text: detail, citations: [], error: true }]);
      toast.error(detail);
    } finally { setBusy(false); }
  };

  const [savingSession, setSavingSession] = useState(false);
  const answerCount = msgs.filter((m) => m.role === "justice" && !m.error && m.text).length;

  const saveSession = async () => {
    const transcript = [];
    for (let i = 0; i < msgs.length; i++) {
      const m = msgs[i];
      if (m.role === "justice" && !m.error && m.text) {
        const qn = msgs[i - 1]?.role === "user" ? msgs[i - 1].text : "";
        transcript.push((qn ? `Q: ${qn}\n` : "") + m.text);
      }
    }
    if (transcript.length === 0) return;
    setSavingSession(true);
    try {
      await api.post("/justice/save-answer", {
        answer: transcript.join("\n\n———\n\n"),
        question: "Justice Q&A research session",
        mode: "timeline",
      });
      toast.success(`Saved ${transcript.length} answer${transcript.length === 1 ? "" : "s"} as a research note`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save session");
    } finally { setSavingSession(false); }
  };

  return (
    <div data-testid="ask-justice" className="max-w-3xl mx-auto">
      <div className="flex items-center gap-3 mb-4">
        <Justice size={64} />
        <div className="flex-1">
          <div className="ek-stamp inline-block mb-1">ASK JUSTICE</div>
          <h1 className="font-head font-bold text-2xl tracking-tight leading-tight">Question your evidence</h1>
        </div>
        {answerCount > 0 && (
          <Button
            onClick={saveSession}
            disabled={savingSession}
            data-testid="save-session-btn"
            variant="outline"
            className="rounded-md shrink-0"
            style={{ borderColor: "var(--ek-border)", background: "transparent", color: "var(--ek-text)" }}
          >
            {savingSession ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <BookmarkPlus className="h-4 w-4 mr-2" style={{ color: "var(--ek-primary)" }} />}
            Save all answers
          </Button>
        )}
      </div>

      <div className="flex items-start gap-2 rounded-md border px-3 py-2 mb-4 text-[11px] leading-relaxed" style={{ borderColor: "var(--ek-border)", background: "var(--ek-page)", color: "var(--ek-text-muted)" }} data-testid="ask-justice-disclaimer">
        <ShieldAlert className="h-3.5 w-3.5 mt-0.5 shrink-0" style={{ color: "var(--ek-warning)" }} />
        <span><b style={{ color: "var(--ek-text-secondary)" }}>Not legal advice.</b> Justice answers only from the evidence you've uploaded to this case, and cites every fact by Evidence ID. It won't speculate or draw legal conclusions.</span>
      </div>

      <div className="space-y-4 mb-4 min-h-[240px]">
        {msgs.length === 0 && (
          <div className="text-center py-8" data-testid="ask-justice-empty">
            <Sparkles className="h-6 w-6 mx-auto mb-3" style={{ color: "var(--ek-primary)" }} />
            <p className="text-sm mb-4" style={{ color: "var(--ek-text-secondary)" }}>
              {evidenceCount > 0
                ? `Ask a question about the ${evidenceCount} item(s) in this case's Evidence Vault.`
                : "Add evidence to this case's Vault, then ask Justice about it."}
            </p>
            {evidenceCount > 0 && (
              <div className="flex flex-col items-center gap-2">
                {SUGGESTIONS.map((s) => (
                  <button key={s} onClick={() => ask(s)} data-testid="ask-suggestion" className="text-sm rounded-full border px-3 py-1.5 transition-colors hover:border-slate-500" style={{ borderColor: "var(--ek-border)", color: "var(--ek-text-secondary)" }}>{s}</button>
                ))}
              </div>
            )}
          </div>
        )}
        {msgs.map((m, i) => (
          <motion.div key={i} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
            className={m.role === "user" ? "flex justify-end" : "flex justify-start"}>
            <div className="rounded-lg px-4 py-3 max-w-[85%]" data-testid={m.role === "justice" ? "justice-answer" : "user-question"}
              style={{ background: m.role === "user" ? "var(--ek-primary)" : "var(--ek-surface)", color: m.role === "user" ? "#fff" : "var(--ek-text)", border: m.role === "user" ? "none" : "1px solid var(--ek-border)" }}>
              <p className="text-sm whitespace-pre-wrap leading-relaxed">{m.text}</p>
              {m.role === "justice" && m.citations && m.citations.length > 0 && (
                <div className="mt-3 pt-2 flex flex-wrap" style={{ borderTop: "1px solid var(--ek-border)" }}>
                  {m.citations.map((c, j) => <Citation key={j} c={c} onOpen={onOpenEvidence} />)}
                </div>
              )}
              {m.role === "justice" && m.cached && (
                <span className="mt-2 inline-flex items-center gap-1 text-[10px]" style={{ color: "var(--ek-secure)" }}><Coins className="h-3 w-3" /> Cached · no credits used</span>
              )}
              {m.role === "justice" && !m.error && m.text && (
                <div className="mt-2 pt-2" style={{ borderTop: m.citations && m.citations.length > 0 ? "none" : "1px solid var(--ek-border)" }}>
                  <button
                    onClick={() => setSaveTarget({ answer: m.text, question: msgs[i - 1]?.role === "user" ? msgs[i - 1].text : "" })}
                    data-testid={`save-answer-${i}`}
                    className="inline-flex items-center gap-1.5 text-xs rounded-md border px-2 py-1 transition-colors hover:border-slate-500"
                    style={{ borderColor: "var(--ek-border)", color: "var(--ek-primary)", background: "var(--ek-page)" }}>
                    <BookmarkPlus className="h-3.5 w-3.5" /> Save to case
                  </button>
                </div>
              )}
              {m.role === "justice" && !m.error && Array.isArray(m.suggestions) && m.suggestions.length > 0 && i === msgs.length - 1 && !busy && (
                <div className="mt-3 pt-2" style={{ borderTop: "1px solid var(--ek-border)" }} data-testid={`justice-followups-${i}`}>
                  <div className="ek-mono-label mb-1.5 flex items-center gap-1" style={{ color: "var(--ek-text-muted)" }}><Sparkles className="h-3 w-3" /> ASK NEXT</div>
                  <div className="flex flex-col items-start gap-1.5">
                    {m.suggestions.map((s, k) => (
                      <button key={k} onClick={() => ask(s)} data-testid={`followup-${i}-${k}`}
                        className="text-left text-xs rounded-full border px-3 py-1.5 transition-colors hover:border-slate-500"
                        style={{ borderColor: "var(--ek-border)", color: "var(--ek-primary)", background: "var(--ek-page)" }}>
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        ))}
        {busy && (
          <div className="flex justify-start"><div className="rounded-lg px-4 py-3" style={{ background: "var(--ek-surface)", border: "1px solid var(--ek-border)" }}>
            <JusticeThinking size={36} compact messages={["Justice is reading your evidence…", "Searching your evidence…", "Linking facts to their sources…", "Preparing a source-cited answer…"]} testid="ask-justice-thinking" />
          </div></div>
        )}
        <div ref={endRef} />
      </div>

      <div className="sticky bottom-20 flex items-end gap-2 rounded-lg border p-2" style={{ background: "var(--ek-surface)", borderColor: "var(--ek-border)" }}>
        <Textarea value={q} onChange={(e) => setQ(e.target.value)} data-testid="ask-justice-input"
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); ask(); } }}
          placeholder="Ask about your evidence…" rows={1}
          className="flex-1 resize-none border-0 bg-transparent focus-visible:ring-0" style={{ color: "var(--ek-text)" }} />
        <Button onClick={() => ask()} disabled={busy || !q.trim()} data-testid="ask-justice-send" className="rounded-md shrink-0" style={{ background: "var(--ek-primary)", color: "#fff" }}>
          <Send className="h-4 w-4" />
        </Button>
      </div>

      <Dialog open={saveTarget != null} onOpenChange={(v) => !v && !saving && setSaveTarget(null)}>
        <DialogContent className="sm:max-w-md" style={{ background: "var(--ek-surface)", borderColor: "var(--ek-border)" }} data-testid="save-answer-dialog">
          <DialogHeader>
            <DialogTitle className="font-head font-semibold tracking-tight flex items-center gap-2">
              <BookmarkPlus className="h-5 w-5" style={{ color: "var(--ek-primary)" }} /> Save this answer to your case
            </DialogTitle>
            <DialogDescription style={{ color: "var(--ek-text-secondary)" }}>
              The answer is saved verbatim and labeled as a documented Q&amp;A summary. Choose where to keep it.
            </DialogDescription>
          </DialogHeader>
          {saveTarget && (
            <div className="rounded-md border p-3 text-xs max-h-32 overflow-y-auto" data-private style={{ borderColor: "var(--ek-border)", background: "var(--ek-page)", color: "var(--ek-text-secondary)" }}>
              {saveTarget.answer}
            </div>
          )}
          <DialogFooter className="flex-col sm:flex-col gap-2">
            <Button onClick={() => saveAnswer("timeline")} disabled={saving} data-testid="save-answer-timeline" className="rounded-md w-full justify-start" style={{ background: "var(--ek-primary)", color: "#fff" }}>
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileSearch className="h-4 w-4 mr-2" />} Add as a timeline record
            </Button>
            <Button onClick={() => saveAnswer("objective")} disabled={saving} data-testid="save-answer-objective" variant="outline" className="rounded-md w-full justify-start" style={{ borderColor: "var(--ek-border)", background: "transparent", color: "var(--ek-text)" }}>
              Append to case objective summary
            </Button>
            <Button onClick={() => saveAnswer("both")} disabled={saving} data-testid="save-answer-both" variant="outline" className="rounded-md w-full justify-start" style={{ borderColor: "var(--ek-border)", background: "transparent", color: "var(--ek-text)" }}>
              Save to both
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
